import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Stethoscope, FolderTree, Package, Percent,
  Images, FileBarChart, ScrollText, Settings as SettingsIcon,
  LogOut, Moon, Sun, Menu, X, ShieldCheck, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/procedures", label: "Procedures", icon: Stethoscope },
  { to: "/categories", label: "Categories", icon: FolderTree },
  { to: "/packages", label: "Packages", icon: Package },
  { to: "/commission", label: "Commission", icon: Percent },
  { to: "/images", label: "Image Library", icon: Images },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { dark, setDark } = useTheme();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-200 dark:border-slate-800">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center text-white font-heading font-bold">Y</div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-sm tracking-tight">Youngness</div>
            <div className="text-[11px] text-slate-400">by Dr. Awish</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-[background-color,color] duration-200 ${
                  isActive ? "bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400" : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-semibold uppercase">
              {(user?.name || "U")[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-slate-400">
                {isAdmin ? <ShieldCheck className="h-3 w-3 text-teal-500" /> : <Eye className="h-3 w-3" />}
                {isAdmin ? "Admin" : "Staff (Read-only)"}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <h1 className="font-heading font-bold text-lg tracking-tight">Pricing & Commission Portal</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" aria-label="Toggle theme">
              {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn" className="gap-2 text-slate-600 dark:text-slate-300">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
